import React, { useCallback, useEffect, useState } from 'react';
import UpgradePrompt from './UpgradePrompt.jsx';
import { BILLING_UPSELL_EVENT, normalizeBillingUpsellDetail } from '../../utils/billingUpsell.js';
import billingService from '../../services/billingService.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { useMode } from '../../context/ModeContext.jsx';
import { useOrganization } from '../../context/OrganizationContext.jsx';

const BillingUpsellProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentMode } = useMode();
  const { currentOrganization } = useOrganization();
  const [prompt, setPrompt] = useState(null);

  const closePrompt = useCallback(() => {
    setPrompt(null);
  }, []);

  useEffect(() => {
    const handleUpsell = (event) => {
      setPrompt(normalizeBillingUpsellDetail(event.detail));
    };

    window.addEventListener(BILLING_UPSELL_EVENT, handleUpsell);
    return () => window.removeEventListener(BILLING_UPSELL_EVENT, handleUpsell);
  }, []);

  useEffect(() => {
    if (!currentUser?._id || !['individual', 'organization'].includes(currentMode)) return undefined;
    const organizationId = currentMode === 'organization' ? currentOrganization?._id : null;
    if (currentMode === 'organization' && !organizationId) return undefined;

    let cancelled = false;
    billingService.getSubscription(organizationId).then((data) => {
      if (cancelled) return;
      const subscription = data?.subscription;
      const trialEnded = subscription?.status === 'expired' && subscription?.trial?.endDate;
      if (!trialEnded) return;

      const acknowledgementKey = `billing_trial_expired_${subscription._id}_${new Date(subscription.trial.endDate).getTime()}`;
      if (window.localStorage.getItem(acknowledgementKey)) return;
      window.localStorage.setItem(acknowledgementKey, new Date().toISOString());

      setPrompt(normalizeBillingUpsellDetail({
        code: 'TRIAL_EXPIRED',
        itemId: 'trial_expired',
        feature: 'Premium workspace features',
        message: 'Your free trial ended and this workspace automatically moved to the Free plan. Premium features are now locked until you choose a paid plan.',
        workspaceType: organizationId ? 'organization' : 'personal'
      }));
    }).catch(() => {
      // Normal API error handling already reports billing failures where needed.
    });

    return () => {
      cancelled = true;
    };
  }, [currentMode, currentOrganization?._id, currentUser?._id]);

  return (
    <>
      {children}
      {prompt && (
        <UpgradePrompt
          variant="modal"
          feature={prompt.feature}
          message={prompt.message}
          limit={prompt.limit}
          current={prompt.current}
          used={prompt.used}
          itemId={prompt.itemId}
          code={prompt.code}
          resetAt={prompt.resetAt}
          workspaceType={prompt.workspaceType}
          upgradeUrl={prompt.upgradeUrl}
          onClose={closePrompt}
        />
      )}
    </>
  );
};

export default BillingUpsellProvider;
